import { useMemo } from 'react'
import { sessionStorageStringSerializer } from '@client-portal-utils/sessionStorage'
import { useSessionStorage } from 'usehooks-ts'

import { Maybe } from '@acre/graphql'

import { renderDynamicTheme } from '../../App.helpers'
import theme from '../../theme/theme.mui'
import { useGetAllCases } from './allCases'

const DEFAULT_CP3_COLOUR = theme.colours.portalOptions.gray

interface UseDynamicThemeProps {
  caseId: Maybe<string>
  clientId: Maybe<string>
  shouldUseOrgColour?: boolean
}

export const useDynamicTheme = ({ caseId, clientId, shouldUseOrgColour = false }: UseDynamicThemeProps) => {
  const { cases: allCases, loading: loadingCases } = useGetAllCases({
    includeOwnerOrganisation: true,
    skip: !clientId,
  })

  const organisationIds = useMemo(
    () => new Set(allCases?.map((caseData) => caseData.details?.owner_organisation?.id)),
    [allCases],
  )
  const allCasesFromSameOrg = organisationIds.size === 1

  const caseData = useMemo(() => (caseId ? allCases?.find((c) => c.id === caseId) : null), [allCases, caseId])
  const caseThemeColour = caseData?.details?.owner_organisation?.client_portal_settings?.primary_colour
  const firstCaseThemeColour = allCases?.[0]?.details?.owner_organisation?.client_portal_settings?.primary_colour

  const [primaryColour] = useSessionStorage<string | null>('primary_colour', null, sessionStorageStringSerializer)

  const selectedColour = useMemo(() => {
    if (!allCases || loadingCases) return primaryColour || DEFAULT_CP3_COLOUR

    if (allCasesFromSameOrg) return firstCaseThemeColour || DEFAULT_CP3_COLOUR
    return shouldUseOrgColour && caseThemeColour ? caseThemeColour : DEFAULT_CP3_COLOUR
  }, [
    primaryColour,
    loadingCases,
    allCases,
    caseThemeColour,
    firstCaseThemeColour,
    shouldUseOrgColour,
    allCasesFromSameOrg,
  ])

  const dynamicTheme = useMemo(() => renderDynamicTheme(selectedColour, theme), [selectedColour])

  return dynamicTheme
}
